/**
 * Эрэмбэлсэн хэвтээ багана — ангиллын хэмжээг хооронд нь харьцуулна
 * (төлөв, нийлүүлэгч, салбар). Хуучин `BarList`-ийг орлоно.
 *
 * Мөр бүр: нэр · тууз · утга · нийтээс эзлэх хувь. `part` өгвөл
 * туузан дотор бараан хэсэг (жишээ нь салбарын гутлаас «бэлэн» нь) —
 * хоёр давхар bar биш, нэг масштаб.
 *
 * ⚠️ Нэг цуврал = нэг өнгө. Урт нь аль хэдийн хэмжээг хэлж байгаа тул
 *    том нь бараан гэж давхар кодлохгүй.
 * ⚠️ Хувь нь зөвхөн харуулах харьцаа — ирсэн утгуудаас, серверийн
 *    тооцоог орлохгүй.
 */

import { formatQty } from '@boots/domain';
import { Empty } from '../ui';

export interface RankRow {
  key: string;
  label: string;
  /** Нэрийн доорх жижиг тайлбар (код г.м.) */
  sub?: string;
  value: number;
  /** `value`-ийн дэд хэсэг — туузан дотор бараанаар */
  part?: number;
}

const FILL = {
  // soft нь ижил өнгөний тунгалаг хувилбар — харанхуй горимд accent-300
  // бараан өнгөтэйгөө нийлж ялгагдахгүй байсан
  accent: { bar: 'bg-chart-1', soft: 'bg-chart-1/35' },
  alert: { bar: 'bg-alert-dot', soft: 'bg-alert-dot/35' },
} as const;

export function RankBars({
  rows,
  format = (n) => `${formatQty(n)} ш`,
  tone = 'accent',
  legend,
  empty = 'Өгөгдөл алга',
}: {
  rows: RankRow[];
  /** Утгын текст. Мөнгөнд `formatRub` г.м. */
  format?: (n: number) => string;
  /** `alert` — зөвхөн анхаарал шаардах утгад (өр) */
  tone?: keyof typeof FILL;
  /** `part` ашиглавал хоёр хэсгийн нэр: [бараан, цайвар] */
  legend?: [string, string];
  empty?: string;
}) {
  if (rows.length === 0) return <Empty>{empty}</Empty>;

  const max = Math.max(1, ...rows.map((r) => r.value));
  const total = rows.reduce((n, r) => n + r.value, 0);
  const fill = FILL[tone];

  return (
    <div className="flex flex-col gap-3">
      {legend ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-meta text-ink-2">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className={`size-2.5 rounded-[3px] ${fill.bar}`} />
            {legend[0]}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className={`size-2.5 rounded-[3px] ${fill.soft}`} />
            {legend[1]}
          </span>
        </div>
      ) : null}

      {/* subgrid — утгын багана бүх мөрд нэг өргөн тул туузууд нэг цэгт дуусна */}
      <ul className="grid grid-cols-[minmax(0,6.5rem)_minmax(3rem,1fr)_auto] gap-x-3 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)_auto]">
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.value / total) * 100) : 0;
          const title =
            `${r.label}: ${format(r.value)}` +
            (legend && r.part !== undefined ? ` · ${legend[0].toLowerCase()} ${format(r.part)}` : '');
          return (
            <li
              key={r.key}
              title={title}
              className="col-span-3 grid grid-cols-subgrid items-center rounded-well px-1.5 py-1.5
                transition hover:bg-row-hover"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-meta">{r.label}</span>
                {r.sub ? <span className="num truncate text-micro text-ink-3">{r.sub}</span> : null}
              </span>

              <span className="relative h-2.5 overflow-hidden rounded-pill bg-track" aria-hidden>
                <span
                  className={`absolute inset-y-0 left-0 rounded-pill ${r.part === undefined ? fill.bar : fill.soft}`}
                  style={{ width: `${bar(r.value, max)}%` }}
                />
                {r.part === undefined ? null : (
                  <span
                    className={`absolute inset-y-0 left-0 rounded-pill ${fill.bar}`}
                    style={{ width: `${bar(r.part, max)}%` }}
                  />
                )}
              </span>

              <span className="flex items-baseline justify-end gap-2 whitespace-nowrap">
                <span className="num text-meta">{format(r.value)}</span>
                <span className="num w-9 text-right text-micro text-ink-3">{pct}%</span>
              </span>
              {/* Дэд хэсгийн утга зөвхөн туузаар харагддаг — уншигчид текстээр */}
              {r.part === undefined ? null : <span className="sr-only">{title}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** 0 биш утга харагдахуйц үлдэнэ — «1» ба «0» ялгаатай. */
function bar(v: number, max: number): number {
  const pct = (v / max) * 100;
  return v > 0 ? Math.max(1.5, pct) : 0;
}
